/**
 * useCreateTableFlow Hook
 *
 * Orchestrates the create table sheet wizard flow.
 * Handles API calls, state management, and step transitions.
 *
 * Features:
 * - File upload with validation via shared utilities
 * - Schema extraction
 * - Version progress tracking with auto-transition to step 2
 * - Field configuration persistence
 * - Table finalization
 * - Error handling with retry support
 *
 * Flow:
 * 1. User enters name + selects file
 * 2. User clicks Upload → stays on step 1 showing progress
 * 3. When processing completes → auto-advance to step 2
 * 4. If processing fails → show error with retry option
 *
 * @module features/workspace/components/CreateTableSheet/hooks/useCreateTableFlow
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  normalizeDataType,
  type TableCreateResponse,
  type TableFieldApiResponse,
  type VersionStatus,
  type CreateTableFieldConfig,
} from '@loader/shared-types';

import { useCreateTableStore, STEP_ORDER } from '../../../stores/useCreateTableStore';
import type { UseCreateTableFlowOptions, UseCreateTableFlowResult } from '../types';

import { useRpc } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';
import { useToastStore } from '@/store/useToasts';
import { useFolderQuery } from '@/features/organization/hooks';
import { useVersionProgress, useVersionWithStorageLimit } from '@/hooks';
import { API_BASE_URL } from '@/config/constants';
import { validateFile } from '@/lib/fileUtils';
import { compressFile } from '@/lib/compression';
import { uploadWithProgress } from '@/lib/uploadWithProgress';
import {
  UPLOAD_PHASE_WEIGHT,
  PROCESSING_PHASE_WEIGHT,
  UPLOAD_PHASE_END_PERCENT,
} from '@/lib/uploadTypes';

// ============================================================================
// TYPES
// ============================================================================

/** Error with optional backend error code */
interface ErrorWithCode extends Error {
  code?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Debounce delay for name checking (ms) */
const NAME_CHECK_DEBOUNCE_MS = 300;

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for managing the create table flow
 *
 * Provides handlers for each step and manages API interactions.
 */
export function useCreateTableFlow(
  options: UseCreateTableFlowOptions = {}
): UseCreateTableFlowResult {
  const { onSuccess, onError } = options;

  // External hooks
  const { getToken } = useAuth();
  const rpc = useRpc();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToastStore();

  // Store state and actions
  // Important: Destructure all needed functions to get stable references
  // Using `store` object directly in useEffect causes infinite loops
  const store = useCreateTableStore();
  const {
    isOpen,
    currentStep,
    folderId,
    tableName,
    file,
    nameExists,
    sheetName,
    workingTableId,
    versionId,
    versionStatus,
    fields,
    isExtracting,
    errorMessage,
    uploadProgress,
    setNameExists,
    setVersionStatus,
    setError,
    setFields,
    setUploadProgress,
    nextStep,
  } = store;

  // Folder query for name display
  const { data: folder } = useFolderQuery(folderId ?? undefined);

  // Use the battle-tested version progress hook for polling
  // This handles all the polling logic with proper cleanup
  const { version: polledVersion, error: pollingError, isPolling } = useVersionProgress(versionId ?? undefined);

  // Storage limit check with paywall dialog
  const { checkStorageBeforeUpload, StorageLimitDialog, isChecking: isCheckingStorage, setShowLimitDialog } =
    useVersionWithStorageLimit();

  // Refs for cleanup
  const nameCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // State for tracking when we're refetching fields before transition
  const [isPreparingNext, setIsPreparingNext] = useState(false);

  // ============================================================================
  // SYNC VERSION STATUS FROM POLLING
  // ============================================================================

  /**
   * Refetch table fields and transition to configure step
   *
   * IMPORTANT: When version completes, we must refetch the table to get
   * the fields that were extracted by DuckDB (they weren't available
   * in the initial create-table response since processing is async).
   */
  const refetchFieldsAndTransition = useCallback(async () => {
    if (!workingTableId) {
      nextStep();
      return;
    }

    setIsPreparingNext(true);

    try {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.tables[':id'].$get({
        param: { id: workingTableId },
      });

      if (res.ok) {
        const { table } = (await res.json()) as { table: { fields: TableFieldApiResponse[] } };
        // Update store with fresh fields from the refetched table
        const refreshedFields: CreateTableFieldConfig[] = table.fields.map(
          (f) => ({
            id: f.id,
            name: f.name,
            dataType: normalizeDataType(f.data_type),
            isRequired: f.is_required,
            isUnique: f.is_unique,
            description: '',
          })
        );
        setFields(refreshedFields);
      }
    } catch (error) {
      // Log error but still transition - user can retry
      console.error('Failed to refetch table fields:', error);
    } finally {
      setIsPreparingNext(false);
    }

    nextStep();
  }, [workingTableId, rpc, setFields, nextStep]);

  /**
   * Sync version status from useVersionProgress hook to store
   * User must click "Continue" button to proceed to configure step
   */
  useEffect(() => {
    if (polledVersion?.status) {
      const status = polledVersion.status as VersionStatus;

      // Only update store if status actually changed (prevents infinite loop)
      if (status !== versionStatus) {
        setVersionStatus(status);
      }

      // If version failed, capture the error message and refresh table list
      // so the draft table appears in the workspace explorer
      if (status === 'failed') {
        if (polledVersion.error_message) {
          setError(polledVersion.error_message);
        }
        void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
        void queryClient.invalidateQueries({ queryKey: queryKeys.dataGrid.tables });
      }

      // Note: No auto-transition - user must click "Continue" button
      // This allows them to see the completed state before proceeding
    }
  }, [polledVersion?.status, polledVersion?.error_message, polledVersion?.id, versionStatus, setVersionStatus, setError, queryClient]);

  /**
   * Detect when version was deleted (draft table cleanup on processing failure).
   *
   * When the DuckDB API fails before sending a callback, the backend deletes
   * the draft table (CASCADE deletes the version). The polling hook gets a 404,
   * stops polling, and returns null + error. Without this effect the UI would
   * stay stuck on the progress view forever because versionStatus never changes
   * from 'processing'.
   */
  useEffect(() => {
    if (
      versionId &&
      pollingError &&
      !isPolling &&
      !polledVersion &&
      versionStatus !== 'failed' &&
      versionStatus !== 'completed'
    ) {
      setVersionStatus('failed');
      setError('Processing failed. The table could not be created. Please try again.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataGrid.tables });
    }
  }, [versionId, pollingError, isPolling, polledVersion, versionStatus, setVersionStatus, setError, queryClient]);

  /**
   * Handle "Continue" button click after upload completes
   * Refetches table fields (populated by DuckDB) then transitions to configure step
   */
  const handleNextAfterUpload = useCallback(() => {
    if (versionStatus === 'completed' && !isPreparingNext) {
      void refetchFieldsAndTransition();
    }
  }, [versionStatus, isPreparingNext, refetchFieldsAndTransition]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const stepNumber = currentStep === 'upload' ? 1 : currentStep === 'configure' ? 2 : 3;
  const totalSteps = STEP_ORDER.length;
  const progress = (stepNumber / totalSteps) * 100;
  const folderName = folder?.name ?? null;

  // ============================================================================
  // NAME CHECKING
  // ============================================================================

  /**
   * Check if table name already exists (debounced)
   */
  useEffect(() => {
    if (!tableName.trim()) {
      setNameExists(false);
      return;
    }

    // Clear previous timeout
    if (nameCheckTimeoutRef.current) {
      clearTimeout(nameCheckTimeoutRef.current);
    }

    // Debounce the check
    nameCheckTimeoutRef.current = setTimeout(async () => {
      try {
        // @ts-expect-error - Hono RPC type inference limitation
        const res = await rpc.api.private.tables['check-name'].$get({
          query: { name: tableName.trim() },
        });

        if (res.ok) {
          const data = (await res.json()) as { exists: boolean };
          setNameExists(data.exists);
        }
      } catch {
        setNameExists(false);
      }
    }, NAME_CHECK_DEBOUNCE_MS);

    return () => {
      if (nameCheckTimeoutRef.current) {
        clearTimeout(nameCheckTimeoutRef.current);
      }
    };
  }, [tableName, rpc, setNameExists]);

  // ============================================================================
  // STEP HANDLERS
  // ============================================================================

  /**
   * Handle upload step completion
   *
   * Creates a working table and uploads the file for processing.
   * Validates file before upload using shared utilities.
   * Checks storage limits before uploading.
   */
  const handleUpload = useCallback(async () => {
    if (!file || !tableName.trim() || nameExists) {
      return;
    }

    // Validate file before upload using shared utilities
    const validation = validateFile(file);
    if (!validation.isValid) {
      const errorMessage = validation.errors.join('. ');
      store.setError(errorMessage);
      showError('Invalid File', errorMessage);
      return;
    }

    // Check storage limit before uploading - shows paywall dialog if limit reached
    const canProceed = await checkStorageBeforeUpload(file.size);
    if (!canProceed) {
      // Reset file selection so user can try with a different/smaller file
      store.setFile(null);
      store.setSheetName(null);
      store.setError(null); // Clear any previous error
      return; // Dialog will be shown by the hook
    }

    // Reset progress FIRST, before showing progress view (setIsExtracting)
    // This prevents stale progress from previous upload being displayed
    setUploadProgress(0);
    store.setError(null);
    store.setIsExtracting(true);

    // Wait for React to render AND paint the 0% progress state before starting upload
    // Double requestAnimationFrame ensures:
    // 1. First rAF: React commits state changes to DOM
    // 2. Second rAF: Browser has actually painted
    // Without this, fast uploads would show progress jumping from 0 to 6%+
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

    try {
      // Compress CSV/JSON files before upload (5-10x smaller transfer)
      const { blob, fileName, compressed } = await compressFile(file);

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', new File([blob], fileName, { type: blob.type }));
      formData.append('name', tableName.trim());
      if (folderId) {
        formData.append('folder_id', folderId);
      }
      // Add sheet name if provided (for Excel workbooks with multiple sheets)
      // The backend will use this to extract data from the specific sheet
      if (sheetName) {
        formData.append('sheet_name', sheetName);
      }
      // Send original file metadata so backend uses it for validation and records
      if (compressed) {
        formData.append('original_file_name', file.name);
        formData.append('original_file_size', String(file.size));
      }

      // Get auth token
      const token = await getToken();

      // Upload file with real progress tracking
      const result = await uploadWithProgress<TableCreateResponse>({
        url: `${API_BASE_URL}/api/private/uploads/create-table`,
        formData,
        token,
        onProgress: (progress) => {
          // Map upload progress (0-100) to display range (0-80)
          const mappedProgress = Math.round(progress * UPLOAD_PHASE_WEIGHT);
          setUploadProgress(mappedProgress);
        },
      });

      if (!result.ok || !result.data) {
        const err = new Error(result.error || 'Failed to create table. Please try again.') as ErrorWithCode;
        err.code = result.errorCode;
        throw err;
      }

      const { table, version } = result.data;

      // Extract field configurations from response with proper typing
      // Normalize data types to handle legacy/inferred types from backend
      const extractedFields: CreateTableFieldConfig[] = table.fields.map(
        (f: TableFieldApiResponse) => ({
          id: f.id,
          name: f.name,
          dataType: normalizeDataType(f.data_type),
          isRequired: f.is_required,
          isUnique: f.is_unique,
          description: '',
        })
      );

      // Update store with working table data
      // Note: We stay on upload step showing progress
      // Auto-transition to configure step happens when version status becomes 'completed'
      store.setWorkingTableData({
        workingTableId: table.id,
        versionId: version.id,
        versionStatus: version.status,
        fields: extractedFields,
      });

      // Invalidate TanStack Query cache to show new table in lists
      await queryClient.invalidateQueries({
        queryKey: queryKeys.tables.all,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.dataGrid.tables,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to create table';
      const errorCode = (error as ErrorWithCode).code;

      // Check if this is a storage or table limit error from the backend (match on error code)
      const isStorageError = errorCode === 'STORAGE_LIMIT_EXCEEDED';
      const isTableLimitError = errorCode === 'TABLE_LIMIT_EXCEEDED';

      if (isStorageError) {
        // Show storage paywall instead of error view
        setShowLimitDialog(true);
        store.setFile(null);
        store.setSheetName(null);
        store.setError(null); // Clear any previous error
      } else if (isTableLimitError) {
        // Close the sheet and show table limit - user shouldn't have gotten here
        // but handle gracefully if backend check is more restrictive
        // Note: no draft exists yet at this point (error is from the create-table API)
        store.closeSheet();
        showError('Table Limit Reached', 'Please upgrade your plan to create more tables.');
      } else {
        store.setError(message);
        showError('Upload Failed', message);
        onError?.(error instanceof Error ? error : new Error(message));
      }
    } finally {
      store.setIsExtracting(false);
    }
  }, [
    file,
    tableName,
    nameExists,
    folderId,
    sheetName,
    getToken,
    store,
    queryClient,
    showError,
    onError,
    checkStorageBeforeUpload,
    setShowLimitDialog,
    setUploadProgress,
  ]);

  /**
   * Handle configure step completion
   *
   * Saves field configurations to the working table.
   */
  const handleConfigure = useCallback(async () => {
    if (!workingTableId || fields.length === 0) {
      return;
    }

    store.setIsSavingFields(true);
    store.setError(null);

    try {
      // Prepare field updates (including data_type for draft tables)
      const updatePayload = {
        fields: fields.map((field) => ({
          id: field.id,
          name: field.name,
          data_type: field.dataType,
          is_required: field.isRequired,
          is_unique: field.isUnique,
          description: field.description || undefined,
        })),
      };

      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.tables[':id'].fields.$put({
        param: { id: workingTableId },
        json: updatePayload,
      });

      if (!res.ok) {
        throw new Error('Failed to save field configuration');
      }

      // Move to review step
      store.nextStep();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save fields';
      store.setError(message);
      showError('Save Failed', message);
    } finally {
      store.setIsSavingFields(false);
    }
  }, [workingTableId, fields, rpc, store, showError]);

  /**
   * Handle final table creation
   *
   * Finalizes the working table and navigates to the new table.
   */
  const handleCreate = useCallback(async () => {
    if (!workingTableId) {
      return;
    }

    store.setIsFinalizing(true);
    store.setError(null);

    try {
      // Finalize the table
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.tables[':id'].finalize.$post({
        param: { id: workingTableId },
      });

      if (!res.ok) {
        throw new Error('Failed to create table');
      }

      // Invalidate queries - must include tables.all for workspace explorer to update
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.tables.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.dataGrid.tables }),
        queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.organization.items.inFolder(folderId),
        }),
      ]);

      // Show success message
      showSuccess('Table Created', `${tableName} has been created successfully`);

      // Close sheet
      store.closeSheet();

      // Call success callback
      onSuccess?.(workingTableId);

      // Navigate to workspace with preview open (stay in current folder if applicable)
      void navigate({
        to: folderId ? '/workspace/folder/$id' : '/workspace',
        params: folderId ? { id: folderId } : undefined,
        search: { preview: workingTableId, type: 'table', expanded: true },
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to create table';
      store.setError(message);
      showError('Creation Failed', message);
      onError?.(error instanceof Error ? error : new Error(message));
    } finally {
      store.setIsFinalizing(false);
    }
  }, [
    workingTableId,
    folderId,
    tableName,
    rpc,
    queryClient,
    store,
    showSuccess,
    showError,
    navigate,
    onSuccess,
    onError,
  ]);

  /**
   * Handle back navigation
   */
  const handleBack = useCallback(() => {
    store.previousStep();
  }, [store]);

  /**
   * Handle retry after upload failure
   *
   * Resets to initial state so user can try again with a fresh form.
   * The failed draft table is already deleted by the backend on processing failure.
   */
  const handleRetry = useCallback(() => {
    const folderId = store.folderId;
    store.reset();
    store.openSheet(folderId);
  }, [store]);

  // ============================================================================
  // COMPUTED VALUES FOR UI
  // ============================================================================

  // Combine upload progress (0-80%) with backend progress (80-100%)
  // During upload phase: use uploadProgress from store (real XHR progress mapped to 0-80)
  // After upload: remap backend progress (0-100) to display range (80-100)
  // Only use backend progress when polledVersion exists, otherwise mappedBackendProgress
  // would always be UPLOAD_PHASE_END_PERCENT (80) even during upload
  // Guard with versionId to avoid stale polledVersion data after retry/reset
  // (useVersionProgress returns the old version for one render cycle before its effect clears it)
  const mappedBackendProgress = polledVersion && versionId
    ? UPLOAD_PHASE_END_PERCENT + Math.round((polledVersion.progress ?? 0) * PROCESSING_PHASE_WEIGHT)
    : 0;
  const versionProgress = mappedBackendProgress > uploadProgress ? mappedBackendProgress : uploadProgress;

  // ============================================================================
  // RETURN VALUE
  // ============================================================================

  return {
    // Sheet controls
    isOpen,
    openSheet: store.openSheet,
    closeSheet: store.closeSheet,

    // Step indicator
    stepNumber,
    totalSteps,
    progress,
    folderName,

    // Upload step state
    isUploading: isExtracting || isCheckingStorage,
    versionStatus,
    versionProgress,
    errorMessage,

    // Step handlers
    handleUpload,
    handleNextAfterUpload,
    handleRetry,
    handleConfigure,
    handleCreate,
    isPreparingNext,
    handleBack,

    // Storage limit dialog
    StorageLimitDialog,
  };
}
